<template>
	<div class="flowchart-container" id="space_flowchart" 
		@mousemove="handleMove" 
		@mouseup="handleUp"
		@mousedown="handleDown">
		<svg width="100%" :height="`${height}px`">
			<space-link v-bind.sync="link" 
				v-for="(link, index) in lines" 
				:key="`link${index}`"
				@editLink="editLink(link.id)">
			</space-link>
		</svg>
		<space-node v-bind.sync="node" 
			v-for="(node, index) in space.nodes" 
			:key="`node${index}`"
			:options="nodeOptions"
			@linkingStart="linkingStart(node.id)"
			@linkingStop="linkingStop(node.id)"
			@deleteDevice="confirmNodeDelete(node.id)"
			@nodeSelected="nodeSelected(node.id, $event)">
		</space-node>

		<div class="devices-container">
			<div class="devices-box">
				<div class="device-title">Input Devices</div>
				<div class="device-item" @click="addNode(allChannels.camera)"><camera-icon/><p>Camera</p></div>
				<div class="device-item" @click="addNode(allChannels.microphone)"><microphone-icon/><p>Microphone</p></div>
				<div class="device-item" @click="addNode(allChannels.gestures)"><hand-pointing-right-icon/><p>Leap Motion Gestures</p></div>
				<div class="device-item" @click="addNode(allChannels.nfc)"><nfc-variant-icon/></i><p>NFC Reader</p></div>
			</div>
			<div class="devices-box">
				<div class="device-title">Output Devices</div>
				<div class="device-item" @click="addNode(allChannels.display)"><monitor-icon/><p>Display</p></div>
				<div class="device-item" @click="addNode(allChannels.projector)"><projector-icon/><p>Projector</p></div>
			</div>
			<div class="devices-box">
				<div class="device-title">Special Devices</div>
				<div class="device-item" @click="addNode(allChannels.interactiveWall)"><projector-screen-icon/><p>Interactive Wall</p></div>
				<div class="device-item" @click="addNode(allChannels.smartLights)"><lightbulb-outline-icon/><p>Smart Lights</p></div>
			</div>
		</div>

		<modal v-if="show.deleteLink" :withOkBtn="true" :withCancelBtn="true" @close="show.deleteLink = false" @ok="deleteLink">
			<div slot="header">Delete Link</div>
			<div slot="body">
				Are you sure you want to delete this link?
			</div>
		</modal>

		<modal v-if="show.deleteDevice" :withOkBtn="true" :withCancelBtn="true" @close="show.deleteDevice = false" @ok="deleteDevice">
			<div slot="header">Delete Node</div>
			<div slot="body">
				Are you sure you want to delete this node?
			</div>
		</modal>

		<modal v-if="show.editedStep" :withOkBtn="true" :withCancelBtn="true" @close="show.editedStep = false" @ok="updateNode">
			<div slot="header">Edit Node</div>
			<div slot="body">
				<div class="form-group">
					<input class="form-input" type="text" v-model="editedStep.title" placeholder="Edit Title">
				</div>
				<div class="form-group">
					<textarea class="form-input" type="text" v-model="editedStep.description" placeholder="Add a small description" rows="5"></textarea>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import SpaceLink from "./SpaceLink.vue";
import SpaceNode from "./SpaceNode.vue";
import { getMousePosition } from "../utils/position";
import Modal from "./modals/Modal";
// Icons
import CameraIcon from "./icons/Camera.vue";
import ControllerIcon from "./icons/Controller.vue";
import HandPointingRightIcon from "./icons/HandPointingRight.vue";
import LightbulbOutlineIcon from "./icons/LightbulbOutline.vue";
import MicrophoneIcon from "./icons/Microphone.vue";
import MonitorIcon from "./icons/Monitor.vue";
import NfcVariantIcon from "./icons/NfcVariant.vue";
import ProjectorIcon from "./icons/Projector.vue";
import ProjectorScreenIcon from "./icons/ProjectorScreen.vue";

import allChannels from "../assets/data/allChannels.json";

export default {
  name: "SpaceFlowchart",
  props: {
    height: {
      type: Number,
      default: 400,
    },
  },
  components: {
    SpaceLink,
    SpaceNode,
    modal: Modal,
    CameraIcon,
    ControllerIcon,
    HandPointingRightIcon,
    LightbulbOutlineIcon,
    MicrophoneIcon,
    MonitorIcon,
    NfcVariantIcon,
    ProjectorIcon,
    ProjectorScreenIcon,
  },
  computed: {
    ...mapGetters(["space"]),
    nodeOptions() {
      return {
        centerY: this.centerY,
        centerX: this.centerX,
        scale: this.scale,
        offsetTop: this.rootDivOffset.top,
        offsetLeft: this.rootDivOffset.left,
        selected: this.action.selected,
      };
    },
    lines() {
      const lines = this.space.links.map((link) => {
        const fromNode = this.findNodeWithID(link.from);
        const toNode = this.findNodeWithID(link.to);
        let x, y, cy, cx, ex, ey;
        x = this.centerX + fromNode.x;
        y = this.centerY + fromNode.y;
        [cx, cy] = this.getPortPosition("bottom", x, y);
        x = this.centerX + toNode.x;
        y = this.centerY + toNode.y;
        [ex, ey] = this.getPortPosition("top", x, y);
        return {
          start: [cx, cy],
          end: [ex, ey],
          id: link.id,
        };
      });
      if (this.draggingLink) {
        let x, y, cy, cx;
        const fromNode = this.findNodeWithID(this.draggingLink.from);
        x = this.centerX + fromNode.x;
        y = this.centerY + fromNode.y;
        [cx, cy] = this.getPortPosition("bottom", x, y);
        // push temp dragging link, mouse cursor postion = link end postion
        lines.push({
          start: [cx, cy],
          end: [this.draggingLink.mx, this.draggingLink.my],
        });
      }
      return lines;
    },
  },
  data() {
    return {
      allChannels: allChannels,
      centerX: 1024,
      scale: 1,
      centerY: 140,
      show: {
        deleteLink: false,
        editedStep: false,
        deleteDevice: false,
      },
      action: {
        linking: false,
        dragging: false,
        scrolling: false,
        selected: 0,
      },
      mouse: {
        x: 0,
        y: 0,
        lastX: 0,
        lastY: 0,
      },
      draggingLink: null,
      rootDivOffset: {
        top: 0,
        left: 0,
      },
      selectedLinkId: null,
      selectedNodeId: null,
      editedStep: {
        id: null,
        title: "",
        description: "",
      },
    };
  },
  mounted() {
    this.rootDivOffset.top = this.$el ? this.$el.offsetTop : 0;
    this.rootDivOffset.left = this.$el ? this.$el.offsetLeft : 0;
  },
  methods: {
    addNode(channel) {
      let maxID = Math.max(
        0,
        ...this.space.nodes.map((link) => {
          return link.id;
        })
      );
      //////////////////////////////////////////////////////////////
      let newNode = Object.assign({}, channel);
      newNode.id = maxID + 1;
      newNode.x = -400;
      newNode.y = 50;
      newNode.items = [];
      //////////////////////////////////////////////////////////////
      this.$store
        .dispatch({
          type: "addSpaceNode",
          newNode: newNode,
        })
        .then((response) => {});
    },
    findNodeWithID(id) {
      return this.space.nodes.find((item) => {
        return id === item.id;
      });
    },
    getPortPosition(type, x, y) {
      if (type === "top") {
        return [x + 30, y];
      } else if (type === "bottom") {
        return [x + 30, y + 60];
      }
    },
    linkingStart(index) {
      this.action.linking = true;
      this.draggingLink = {
        from: index,
        mx: 0,
        my: 0,
      };
    },
    linkingStop(index) {
      // add new Link
      if (this.draggingLink && this.draggingLink.from !== index) {
        // check link existence
        const existed = this.space.links.find((link) => {
          return link.from === this.draggingLink.from && link.to === index;
        });
        if (!existed) {
          let maxID = Math.max(
            0,
            ...this.space.links.map((link) => {
              return link.id;
            })
          );
          const newLink = {
            id: maxID + 1,
            from: this.draggingLink.from,
            to: index,
          };
          this.$store
            .dispatch({
              type: "addSpaceLink",
              newLink: newLink,
            })
            .then((response) => {
              this.draggingLink = null;
            });
        }
      }
    },
    editLink(linkId) {
      this.show.deleteLink = true;
      this.selectedLinkId = linkId;
    },
    deleteLink() {
      let linkId = this.selectedLinkId;
      const deletedLink = this.space.links.find((item) => {
        return item.id === linkId;
      });
      if (deletedLink) {
        this.$store
          .dispatch({
            type: "deleteSpaceLink",
            linkId: linkId,
          })
          .then((response) => {
            this.selectedLinkId = null;
          });
      }
      this.show.deleteLink = false;
    },
    nodeSelected(id, e) {
      this.action.dragging = id;
      this.action.selected = id;
      this.$emit("nodeClick", id);
      this.mouse.lastX =
        e.pageX || e.clientX + document.documentElement.scrollLeft;
      this.mouse.lastY =
        e.pageY || e.clientY + document.documentElement.scrollTop;
      // Reorder story-nodes in DOM to bring them in front of the others
      setTimeout(function () {
        let selectedNode = document.getElementsByClassName(
          "story-node selected"
        )[0];
        document.getElementById("space_flowchart").appendChild(selectedNode);
      }, 300);
    },
    handleMove(e) {
      if (this.action.linking) {
        [this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
        [this.draggingLink.mx, this.draggingLink.my] = [
          this.mouse.x,
          this.mouse.y,
        ];
      }
      if (this.action.dragging) {
        this.mouse.x =
          e.pageX || e.clientX + document.documentElement.scrollLeft;
        this.mouse.y =
          e.pageY || e.clientY + document.documentElement.scrollTop;
        let diffX = this.mouse.x - this.mouse.lastX;
        let diffY = this.mouse.y - this.mouse.lastY;
        this.mouse.lastX = this.mouse.x;
        this.mouse.lastY = this.mouse.y;
        this.moveSelectedNode(diffX, diffY);
      }
      if (this.action.scrolling) {
        [this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
        let diffX = this.mouse.x - this.mouse.lastX;
        let diffY = this.mouse.y - this.mouse.lastY;
        this.mouse.lastX = this.mouse.x;
        this.mouse.lastY = this.mouse.y;
        this.centerX += diffX;
        this.centerY += diffY;
        // this.hasDragged = true
      }
    },
    handleUp(e) {
      const target = e.target || e.srcElement;
      if (this.$el.contains(target)) {
        if (
          typeof target.className !== "string" ||
          target.className.indexOf("node-input") < 0
        ) {
          this.draggingLink = null;
        }
      }
      this.action.linking = false;
      this.action.dragging = null;
      this.action.scrolling = false;
    },
    handleDown(e) {
      const target = e.target || e.srcElement;
      // console.log('for scroll', target, e.keyCode, e.which)
      if (
        (target === this.$el || target.matches("svg, svg *")) &&
        e.which === 1
      ) {
        this.action.scrolling = true;
        [this.mouse.lastX, this.mouse.lastY] = getMousePosition(this.$el, e);
        this.action.selected = null; // deselectAll
      }
      this.$emit("canvasClick", e);
    },
    moveSelectedNode(dx, dy) {
      let index = this.space.nodes.findIndex((item) => {
        return item.id === this.action.dragging;
      });
      let left = this.space.nodes[index].x + dx / this.scale;
      let top = this.space.nodes[index].y + dy / this.scale;
      ////////////////////////////////////////////////////////
      this.$store.dispatch({
        type: "updateSpaceNodePosition",
        nodeIndex: index,
        left: left,
        top: top,
      });
    },
    confirmNodeDelete(id) {
      this.show.deleteDevice = true;
      this.selectedNodeId = id;
    },
    deleteDevice() {
      let nodeId = this.selectedNodeId;
      this.$store
        .dispatch({
          type: "deleteSpaceNode",
          nodeId: nodeId,
        })
        .then((response) => {
          this.selectedNodeId = null;
        });
      this.show.deleteDevice = false;
    },
  },
};
</script>

<style scoped lang="scss">
.flowchart-container {
  margin: 0;
  background: rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  svg {
    cursor: grab;
  }
}

.devices-container {
  position: absolute;
  top: 0;
  left: 20px;
  width: 20%;
  height: 84vh;
  z-index: 50;
  padding: 10px 10px 10px 0;
  margin: 5px 0 5px;
  overflow: scroll;
  min-width: 248px;
}

.devices-box {
  border: 1px solid #a3a5a8;
  border-radius: 4px;
  background: rgb(72, 74, 81);
  margin-bottom: 10px;
  min-width: 220px;
}

.device-title {
  padding: 10px;
  font-size: 16px;
  font-weight: 500;
  background-color: #33363d;
  border-bottom: 1px solid #a3a5a8;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.device-item {
  display: inline-block;
  padding: 10px;
  font-size: 30px;
  width: 50%;
  vertical-align: text-top;
  cursor: pointer;
  p {
    font-size: 14px;
    margin: 0;
  }
}
</style>